import React, { useState, useEffect } from 'react'
import { Link } from 'react-router'
import { FaCheck } from 'react-icons/fa'
import { Box, Stack, ResponsiveStack, Badge, Button, Heading, Text, Select, Textarea, EmptyBox, SimpleTable, SimpleModal, UserChip, UserChooser, FormControl, Card, Icon } from '@/components/lib'
import utils from '@/utils/utils'
import api from '../../../api'
import useStore from '../../../store'

import QueriesImage from '../../../images/queries.png'

export default function List () {
  const [queries, setQueries] = useState([])
  const [statusFilter, setStatusFilter] = useState('open')
  const [assigneesFilter, setAssigneesFilter] = useState([])
  const [resolvingIds, setResolvingIds] = useState([])
  const { trial, getTrialUsers, dataTypes, user, userId } = useStore()
  const trialId = trial?._id
  const users = getTrialUsers(trialId)

  useEffect(() => {
    if (!trialId) return
    api.queries.getForTrial(trialId, d => setQueries(d.queries))
  }, [trialId])

  function updateQuery (query) {
    const newQueries = queries.map(q => q._id === query._id ? query : q)
    setQueries(newQueries)
  }

  const canWriteQueries = utils.canWriteTrialQueries(user, trial)

  function canResolve (query) {
    if (!query || query.status !== 'open') return false
    if (canWriteQueries) return true
    const assignees = query.assignees || []
    return userId && assignees.includes(userId)
  }

  function resolveQuery (queryId) {
    if (!queryId) return
    if (resolvingIds.includes(queryId)) return
    setResolvingIds([...resolvingIds, queryId])
    api.queries.update(queryId, { status: 'closed' }, d => {
      setResolvingIds(ids => ids.filter(id => id !== queryId))
      updateQuery(d)
      utils.toast('success', 'Query resolved')
    }, err => {
      setResolvingIds(ids => ids.filter(id => id !== queryId))
      utils.toast('error', 'Unable to resolve query', err.message)
    })
  }

  let filteredQueries = queries
  if (statusFilter) filteredQueries = filteredQueries.filter(q => q.status === statusFilter)
  if (assigneesFilter?.length > 0) {
    filteredQueries = filteredQueries.filter(q => {
      const qAssignees = q.assignees || []
      return assigneesFilter.every(a => qAssignees.includes(a))
    })
  }

  return (
    <Stack gap={5}>
      <Box>
        <Heading as='h2' size='lg'>Trial queries</Heading>
        <Text>Questions or requests for clarification about the trial.</Text>
      </Box>
      {queries.length > 0
        ? (
          <Stack gap={5}>
            <Stack direction='row' alignItems='center'>
              <Select size='sm' value={statusFilter} onChange={e => setStatusFilter(e.target.value)} w='200px'>
                <option value=''>All statuses</option>
                <option value='open'>Open queries</option>
                <option value='closed'>Closed queries</option>
              </Select>
              <Box w='300px'>
                <UserChooser
                  size='sm'
                  multiple
                  placeholder='All assignees'
                  users={users}
                  selectedUsers={assigneesFilter}
                  onChosen={u => setAssigneesFilter(u || [])}
                />
              </Box>
            </Stack>

            {filteredQueries.length === 0
              ? (
                <EmptyBox image={QueriesImage} title='No queries match your filters' description='Try changing the filters or check back later.' />)
              : (
                <SimpleTable
                  asCard
                  tableProps={{ size: 'sm' }}
                  headers={['Title', 'Data type', 'Status', 'Date', 'Raised by', 'Assigned to', '']}
                  rows={filteredQueries.map(query => {
                    const queryDataType = dataTypes?.find(d => String(d._id) === String(query.dataType))
                    return [
                      query.title,
                      !query.dataType
                        ? <Badge key={`datatype-${query._id}`} colorPalette='purple'>Whole response</Badge>
                        : queryDataType
                          ? <Badge key={`datatype-${query._id}`} colorPalette='blue'>{queryDataType.name} ({queryDataType.type})</Badge>
                          : <Badge key={`datatype-${query._id}`} colorPalette='orange'>Unknown data type</Badge>,
                      <Badge key={query._id} colorPalette={query.status === 'open' ? 'red' : 'green'}>{query.status}</Badge>,
                      utils.formatDateTime(query.createdAt),
                      query.raisedByUser
                        ? (
                          <UserChip compact user={query.raisedByUser} />)
                        : (
                            'Unknown user'),
                      (query.assigneesUsers?.length > 0)
                        ? (
                          <Stack direction='row' gap={2} wrap='wrap'>
                            {query.assigneesUsers.map(u => (
                              <UserChip key={u._id} compact user={u} />
                            ))}
                          </Stack>
                          )
                        : (
                          <Badge colorPalette='orange'>No one</Badge>),
                      <Stack direction='row' justifyContent='end' key={query._id}>
                        <QueryManager
                          trigger={<Button size='sm' colorPalette='primary'>{query.status === 'open' ? 'Review' : 'Manage'}</Button>}
                          query={query}
                          users={users}
                          dataTypes={dataTypes}
                          onChange={updateQuery}
                        />
                        {canResolve(query) && (
                          <Button
                            size='sm'
                            variant='ghost'
                            colorPalette='green'
                            isLoading={resolvingIds.includes(query._id)}
                            onClick={() => resolveQuery(query._id)}
                          >
                            <Icon as={FaCheck} />
                          </Button>
                        )}
                      </Stack>
                    ]
                  })}
                />)}
          </Stack>)
        : (
          <EmptyBox icon='box' title='No queries have been raised yet' description='Queries raised automatically or by staff will appear here.' />)}
    </Stack>
  )
}

function QueryManager ({ trigger, query, onChange, users, dataTypes }) {
  const [isOpen, setIsOpen] = useState(false)
  const [status, setStatus] = useState(query.status)
  const [assignees, setAssignees] = useState(query.assignees || [])
  const [notes, setNotes] = useState(query.notes || '')
  const queryDataType = dataTypes?.find(d => String(d._id) === String(query.dataType))

  function save () {
    const data = { status, assignees, notes }
    api.queries.update(query._id, data, d => {
      setIsOpen(false)
      onChange(d)
    }, err => {
      utils.toast('error', 'Unable to save query', err.message)
    })
  }

  return (
    <>
      {React.cloneElement(trigger, { onClick: () => setIsOpen(true) })}
      <SimpleModal
        size='xl'
        isOpen={isOpen} onClose={() => setIsOpen(false)}
        header='Manage query'
        body={
          <ResponsiveStack spacing={5}>
            <Card variant='elevated' gap={5} maxW='400px'>
              <Heading size='md'>Query details</Heading>
              <Box>

                <Text fontWeight='bold'>{query.participant ? 'Participant' : 'Questionnaire submission'}</Text>
                {query.participant
                  ? <Link to={`/trials/${query.trial}/participants/${query.participant}`}>{query.participantParticipant?.id}</Link>
                  : <Link to={`/trials/${query.trial}/poll-responses?stage=${query.stage}&response=${query.response}`}>View the submission</Link>}
              </Box>
              <Box>
                <Heading size='sm'>{query.title}</Heading>
                <Text>{query.description}</Text>
              </Box>

              <FormControl label='Data type'>
                {!query.dataType
                  ? <Badge colorPalette='purple'>Whole response</Badge>
                  : queryDataType
                    ? <Badge colorPalette='blue'>{queryDataType.name} ({queryDataType.type})</Badge>
                    : <Badge colorPalette='orange'>Unknown data type</Badge>}
              </FormControl>

              {query.dataType &&
                <FormControl label='Question'>
                  <Textarea
                    readOnly
                    fontSize='sm'
                    rows={4}
                    value={query.componentTitle || ''}
                    placeholder='Unknown question'
                  />
                </FormControl>}

              <FormControl label='Status'>
                <Select value={status} onChange={e => setStatus(e.target.value)}>
                  <option value='open'>Open</option>
                  <option value='closed'>Closed</option>
                </Select>
              </FormControl>

              <FormControl label='Assignees'>
                <UserChooser
                  multiple
                  placeholder='No one is assigned'
                  users={users}
                  selectedUsers={assignees}
                  onChosen={u => setAssignees(u || [])}
                />
              </FormControl>

              {query.dataType
                ? (
                  <FormControl label='Response data' helperText={query.responseData?.score !== undefined && query.responseData?.score !== null ? `Scored ${query.responseData.score} in this response` : undefined}>
                    {/* A query can now be raised on a field that was asked and left
                        blank, which has no response data at all — say so rather than
                        stringifying undefined into a controlled textarea. */}
                    <Textarea
                      readOnly fontSize='sm' rows={5} fontFamily='monospace'
                      value={query.responseData
                        ? JSON.stringify(query.responseData.value ?? null, null, 2)
                        : 'No answer was given for this field.'}
                    />
                  </FormControl>)
                : (
                  <FormControl label='Response score' helperText='The score of the whole submission when this query was raised. Later submissions to the same response may have changed it.'>
                    <Text fontWeight='bold'>{query.responseScore ?? 'No score was recorded.'}</Text>
                  </FormControl>)}
            </Card>

            <Stack gap={3} p={3} flex={1}>
              <Heading size='md'>Query notes</Heading>
              <Textarea placeholder='Write notes or updates about this query...' value={notes} onChange={e => setNotes(e.target.value)} rows={20} />
            </Stack>
          </ResponsiveStack>
}
        footer={
          <Stack direction='row' justifyContent='end'>
            <Button onClick={() => setIsOpen(false)}>Cancel</Button>
            <Button onClick={save} colorPalette='primary'>Save</Button>
          </Stack>
}
      />
    </>
  )
}
